/**
 * The Dog profile — the whole record, as the client can read it.
 *
 * WHAT IS DIFFERENT FROM THE PROTOTYPE, AND WHY
 * ---------------------------------------------
 * The prototype rendered 33 rows against one complete sample dog, so every row always had a value
 * and every section always had content. Against the 199 live records that is not the world: the
 * care dates were only added to the form in July, most weight values are prose, and a meal record
 * is only confidently linked for a minority of dogs. So every row here can be absent, and absence
 * is DESIGNED — a row says "Not on file", and a group where nothing at all is filled in says what
 * is missing and how to fix it instead of showing a wall of blanks.
 *
 * Nothing on this screen is spelled out. Every value is read off `DogPayload`, and the derived
 * things (the care schedule, the food plan, the hero line) were derived on the SERVER — the client
 * formats them with the shared vocabulary and never recomputes a due date.
 *
 * THE ONE PIECE OF DATA THAT IS NOT IN THE PAYLOAD is the emergency contact. Those ~156 people
 * never filled in a form and never saw a privacy notice, so it is a separate endpoint, it is only
 * ever fetched when the client deliberately asks, and every reveal is audited server-side. The UI
 * has to make that deliberateness visible rather than hide the cost behind a pretty row.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import {
  ageLabel,
  fmtLongDay,
  parseDay,
  parseWeight,
  todayAtNoon,
  type CareItem,
  type DogPayload,
  type EmergencyContactPayload,
  type OwnerDetails,
  type RecordDocument,
} from '@ft/shared'

import { getEmergencyContact, type ApiFailure } from '../api/client'
import { Icon, UI_ICON_PATHS } from '../model/icons'
import { usePortal, useSelectedDog, type TabId } from '../state/portal'

// ── The filter strip ────────────────────────────────────────────────────────────────────────────

/**
 * A section id is a tab id minus `all`.
 *
 * This is the type-level version of the prototype's most quietly destructive failure: a tab whose
 * id had no matching section showed an empty screen, and a section with no tab was unreachable —
 * neither said anything. `buildSections` returns a `Record<SectionId, …>`, so a missing section is
 * a compile error and an extra one is unrepresentable.
 */
type SectionId = Exclude<TabId, 'all'>

const SECTION_IDS: readonly SectionId[] = ['identity', 'medical', 'care', 'owner', 'documents']
const TAB_IDS: readonly TabId[] = ['all', ...SECTION_IDS]

const TAB_LABEL: Readonly<Record<TabId, string>> = {
  all: 'Everything',
  identity: 'Identity',
  medical: 'Medical',
  care: 'Care',
  owner: 'Owner',
  documents: 'Documents',
}

/**
 * dc.html:654-655, verbatim. Two whole-string style swaps, not per-property interpolation.
 *
 * `font: inherit` comes FIRST and the size and weight follow it, because the shorthand resets both
 * — a bare `<button>` otherwise renders in the UA's own font, and the design system sets no global
 * button font. React writes style properties in object-literal order, so this order is the rule.
 */
const TAB_BASE: CSSProperties = {
  flex: 'none',
  border: '0',
  cursor: 'pointer',
  padding: '11px 16px',
  borderRadius: '999px',
  font: 'inherit',
  fontSize: '13.5px',
  fontWeight: '600',
  background: 'var(--color-neutral-200)',
  color: 'var(--color-neutral-700)',
}

const TAB_SELECTED: CSSProperties = {
  ...TAB_BASE,
  fontWeight: '700',
  background: 'var(--color-accent)',
  color: 'var(--color-bg)',
}

// ── Row and card recipes (dc.html:190-208) ──────────────────────────────────────────────────────

const CARD: CSSProperties = { gap: '0', padding: '20px', marginBottom: '14px' }

const CARD_HEAD: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '4px',
}

const ROW: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(110px, .7fr) 1.3fr',
  gap: '12px',
  padding: '12px 0',
  borderBottom: '1px solid var(--color-divider)',
  alignItems: 'start',
}

const ROW_LABEL: CSSProperties = { fontSize: '13px' }

/** `overflow-wrap:anywhere` is the only thing stopping a 15-digit microchip breaking the grid. */
const ROW_VALUE: CSSProperties = {
  textAlign: 'right',
  fontWeight: '600',
  fontSize: '14px',
  overflowWrap: 'anywhere',
}

/** A missing value is lighter than a real one, so the two can never be mistaken for each other. */
const ROW_VALUE_ABSENT: CSSProperties = { fontWeight: '400' }

/** Right-aligned cluster for the rows whose value is a control rather than a sentence. */
const VALUE_CLUSTER: CSSProperties = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '8px',
}

const GROUP_CAPTION: CSSProperties = {
  fontSize: '11px',
  fontWeight: '700',
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-700)',
  margin: '18px 0 2px',
}

/**
 * The section's ghost button. The design system's `.btn-ghost` computes to about 34px, below the
 * 44px tap-target floor the rest of this design holds to; every other control on the screen carries
 * the same minimum.
 */
const GHOST_ACTION: CSSProperties = { fontSize: '13px', minHeight: '44px' }

const NOT_ON_FILE = 'Not on file'

// ── Reading the record honestly ─────────────────────────────────────────────────────────────────

/** Blank, whitespace and "not answered" are the same thing: we do not hold it. */
function clean(value: string | null | undefined): string | null {
  const text = (value ?? '').trim()
  return text.length > 0 ? text : null
}

/**
 * Join the parts that exist.
 *
 * The prototype concatenated these unguarded, which was right for its one complete sample and wrong
 * for real data: a dog with no sex recorded rendered a bare " · Entire", and an owner with no
 * postcode rendered an address with a trailing comma.
 */
function joined(parts: readonly (string | null | undefined)[], separator: string): string | null {
  const kept = parts.map((part) => (part ?? '').trim()).filter((part) => part.length > 0)
  return kept.length > 0 ? kept.join(separator) : null
}

/** A day, or nothing. Never "Invalid Date", and never the em-dash — absence has its own treatment. */
function day(value: string | null | undefined): string | null {
  const parsed = parseDay(clean(value))
  return parsed ? fmtLongDay(parsed) : null
}

/**
 * Read a yes/no answer without flattening anything else.
 *
 * The live form's answers are free-ish strings. Mapping only the two we recognise and passing
 * anything else through verbatim is what stops a "Being treated" turning into a confident "No".
 */
function yesNo(value: string | null | undefined, whenYes: string, whenNo: string): string | null {
  const text = clean(value)
  if (text === null) return null
  if (/^yes\b/i.test(text)) return whenYes
  if (/^no\b/i.test(text)) return whenNo
  return text
}

/**
 * A document URL must be a path on THIS origin.
 *
 * JotForm's own upload URLs are anonymously downloadable — no login, no token, no expiry — so one
 * reaching an `href` would publish a client's vaccination card to anyone holding the link. The wire
 * contract says the server never sends one; this is the second lock, because being wrong once is
 * not recoverable.
 */
function isPortalPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//')
}

// ── The shape of a profile ──────────────────────────────────────────────────────────────────────

interface ProfileRow {
  readonly key: string
  readonly label: string
  /** The plain-text value. `null` means we do not hold it, and gets the designed absent treatment. */
  readonly text: string | null
  /** A bespoke right-hand side: a document link, a care tag, the masked emergency contact. */
  readonly node: ReactNode
  /** Words that should match a search but are not shown (a document's kind, a field's other name). */
  readonly keywords: string
}

/** What a group says when it holds nothing. Never a blank card, never a silent absence. */
interface EmptyCopy {
  readonly headline: string
  readonly help: string
}

interface ProfileGroup {
  readonly key: string
  /** Drawn only where a section holds more than one kind of thing. */
  readonly caption: string | null
  readonly rows: readonly ProfileRow[]
  readonly empty: EmptyCopy
}

interface ProfileSection {
  readonly id: SectionId
  readonly title: string
  /** What the ghost button says when the client taps it. v1 is read-only; this is the way in. */
  readonly ask: string
  readonly groups: readonly ProfileGroup[]
}

function row(key: string, label: string, text: string | null, keywords = ''): ProfileRow {
  return { key, label, text, node: null, keywords }
}

function customRow(key: string, label: string, keywords: string, node: ReactNode): ProfileRow {
  return { key, label, text: null, node, keywords }
}

function group(
  key: string,
  caption: string | null,
  rows: readonly ProfileRow[],
  empty: EmptyCopy,
): ProfileGroup {
  return { key, caption, rows, empty }
}

/**
 * Should this group show its explanation instead of its rows?
 *
 * Two different reasons, and the second one has a trap in it. A group with no rows at all has
 * nothing to draw either way. A group whose every row is blank is the "wall of Not on file" case —
 * but ONLY while nobody is searching: a search for "microchip" against a record that holds no
 * microchip must answer "Microchip — Not on file", because that is the answer. Getting this wrong
 * put a live count of "1 detail matches" above a card that showed no detail at all.
 */
function showsEmptyCopy(g: ProfileGroup, searching: boolean): boolean {
  if (g.rows.length === 0) return true
  return !searching && g.rows.every((r) => r.text === null && r.node === null)
}

// ── Building the sections ───────────────────────────────────────────────────────────────────────

function identitySection(dog: DogPayload, name: string, today: Date): ProfileSection {
  const id = dog.record.identity
  const born = parseDay(id.dateOfBirth)
  const dob = day(id.dateOfBirth)
  const age = ageLabel(born, today)
  const weight = parseWeight(id.weightRaw)
  const neutered = yesNo(id.neutered, 'Neutered', 'Entire')

  const rows: ProfileRow[] = [
    row('breed', 'Breed', clean(id.breed)),
    // `dobIsApprox` is a real field: a birthday the owner guessed must not read as a certainty.
    row('dob', 'Date of birth', dob === null ? null : id.dobIsApprox ? `${dob} · approximate` : dob),
    row('age', 'Age', age.length > 0 ? age : null),
    row('sex', 'Sex', joined([clean(id.sex), neutered], ' · ')),
  ]

  // Only ask about the neutering age of a dog we know IS neutered. On an entire dog the row is
  // nonsense; on a dog whose form was left blank it invents a question nobody was asked.
  if (neutered === 'Neutered') {
    rows.push(row('neuterAge', 'Age when neutered', clean(id.neuterAge)))
  }

  rows.push(
    row('microchip', 'Microchip', clean(id.microchip), 'chip number'),
    row('size', 'Size', clean(id.size)),
    // parseWeight, never `weightRaw + ' kg'`: a third of the live values are prose, and the
    // prototype's concatenation turned "19kg" into "19kg kg" the first time it met a real record.
    row('weight', 'Weight', weight.raw.length > 0 ? weight.display : null, 'kg'),
    row('source', 'From', clean(id.source), 'breeder rescue rehomed'),
    row('acquired', 'Age when you got them', clean(id.ageWhenAcquired)),
    row('description', 'Description', clean(id.description)),
    row('markings', 'Markings', clean(id.markings)),
  )

  return {
    id: 'identity',
    title: `About ${name}`,
    ask: `Tell us at your next visit and we’ll correct ${name}’s details.`,
    groups: [
      group('identity', null, rows, {
        headline: `We hold almost nothing about ${name} yet`,
        help: 'Bring the details along to your next visit and we’ll fill this in with you.',
      }),
    ],
  }
}

function medicalSection(dog: DogPayload, name: string): ProfileSection {
  const med = dog.record.medical
  const insured = yesNo(med.insured, 'Insured', 'Not insured')

  const rows: ProfileRow[] = [
    row('vet', 'Vet practice', clean(med.vetPractice)),
    row('vetPhone', 'Vet phone', clean(med.vetPhone)),
    row('vetAddress', 'Vet address', clean(med.vetAddress)),
    row(
      'insurance',
      'Insurance',
      insured === 'Not insured'
        ? 'Not insured'
        : joined([clean(med.insurer), clean(med.policyNumber)], ' · ') ?? insured,
      'insurer policy',
    ),
  ]

  if (insured !== 'Not insured') {
    rows.push(row('insuranceExpiry', 'Policy renews', day(med.insuranceExpiry)))
  }

  rows.push(
    row('vaccinations', 'Vaccinations up to date', clean(med.vaccinationsUpToDate)),
    row('kennelCough', 'Kennel cough', yesNo(med.kennelCoughReceived, 'Vaccinated', 'Not given')),
    row(
      'medication',
      'Medication',
      yesNo(med.onMedication, clean(med.medicationDetail) ?? 'Yes', 'None'),
    ),
    row('health', 'Health notes', clean(med.healthIssues), 'conditions allergies'),
  )

  return {
    id: 'medical',
    title: 'Medical & vet',
    ask: `Tell us at your next visit and we’ll update ${name}’s medical details.`,
    groups: [
      group('medical', null, rows, {
        headline: 'No medical details on file yet',
        help: 'Your vet’s name and phone number are the two we need first — tell us at your next visit.',
      }),
    ],
  }
}

function careSection(dog: DogPayload, name: string): ProfileSection {
  // The schedule is derived on the server so this screen and Reminders provably cannot disagree.
  // Feeding is not a due date and has its own group below.
  const treatments = dog.care.filter((item) => item.kind !== 'feeding')

  const treatmentRows = treatments.map((item) =>
    customRow(
      `care-${item.kind}`,
      item.name,
      `${item.detail} ${item.status}`,
      <CareValue item={item} />,
    ),
  )

  const foodRows: ProfileRow[] = []
  let foodEmpty: EmptyCopy = {
    headline: `We don’t have a feeding plan for ${name} yet`,
    help: 'Tell us what and how much they eat and we’ll record it, so every day here is the same as home.',
  }

  if (dog.food.state === 'available') {
    const plan = dog.food.plan
    foodRows.push(
      row('meals', 'Meals', plan.dailyLine, 'how often supplements medication'),
      row('foodTypes', 'Food', plan.typeLine, 'kibble wet sachet tin'),
      // `dailyPortion` says "Portion not recorded" when nothing parsed. That is the same fact as an
      // absent row, so it goes through the one absent treatment rather than a second vocabulary.
      row('portion', 'Daily amount', plan.portionUnknown ? null : plan.dailyPortion, 'cup scoop handful'),
      row('mealtimes', 'Mealtimes', plan.slots.map((slot) => slot.name).join(' · '), 'breakfast lunch dinner'),
      row('foodNotes', 'Food notes', clean(plan.notes)),
    )
  } else if (dog.food.state === 'unconfirmed') {
    foodEmpty = {
      headline: 'We think we have a feeding plan, but it isn’t confirmed',
      help: `Meal records are matched by dog name and surname, which is not reliable enough to show you one — we’d rather ask than show you another dog’s plan. We’ll confirm ${name}’s with you.`,
    }
  }

  const behaviour = dog.record.behaviour
  const behaviourRows: ProfileRow[] = [
    row('recall', 'Recall', clean(behaviour.recall)),
    row('flight', 'Flight risk', clean(behaviour.flightRisk), 'escape bolts'),
    row('dogs', 'With other dogs', clean(behaviour.withOtherDogs)),
    row('obedience', 'Obedience', clean(behaviour.obedience), 'training'),
    row('foodPossessive', 'Around food', clean(behaviour.foodPossessive), 'possessive'),
    row('guarding', 'Guards things', clean(behaviour.resourceGuarding), 'resource guarding toys'),
    row('growled', 'Has growled', clean(behaviour.hasGrowled)),
    row('bitten', 'Has bitten', clean(behaviour.hasBitten), 'bite history'),
    row('people', 'With people', clean(behaviour.likesHumanInteraction), 'strangers'),
    row('house', 'House trained', clean(behaviour.houseTrained), 'toilet'),
    row('crate', 'Crate trained', clean(behaviour.crateTrained)),
    row('toilet', 'Asks to go out', clean(behaviour.toiletIndication), 'toilet indication'),
    row('chews', 'Chews things', clean(behaviour.chewsObjects)),
    row('sleeps', 'Sleeps', clean(behaviour.sleepsWhere), 'crate bed'),
    row('risks', 'Anything else to watch', clean(behaviour.riskyBehaviours), 'risky behaviour'),
  ]

  return {
    id: 'care',
    title: 'Care & feeding',
    ask: `Tell us at your next visit and we’ll update how we care for ${name}.`,
    groups: [
      group('treatments', 'Treatments', treatmentRows, {
        headline: `No treatment dates on file for ${name}`,
        help: 'Tell us when worming and flea treatment were last given, and how often, and we’ll keep track of what’s next.',
      }),
      group('food', 'Feeding', foodRows, foodEmpty),
      group('behaviour', 'Behaviour & handling', behaviourRows, {
        headline: 'Nothing recorded about how they get on',
        help: `Recall, other dogs and anything to watch for — the more we know, the better ${name}’s day is.`,
      }),
    ],
  }
}

function ownerSection(dog: DogPayload, owner: OwnerDetails, name: string): ProfileSection {
  const rows: ProfileRow[] = [
    row('ownerName', 'Owner', clean(owner.name)),
    row('mobile', 'Mobile', clean(owner.mobile), 'phone number'),
    row('home', 'Home phone', clean(owner.homePhone)),
    row('work', 'Work phone', clean(owner.workPhone)),
    row('email', 'Email', clean(owner.email)),
    row('address', 'Address', joined([clean(owner.address), clean(owner.postcode)], ', ')),
    row(
      'partner',
      'Second parent',
      joined([clean(owner.partnerName), clean(owner.partnerMobile), clean(owner.partnerEmail)], ' · '),
      'partner other half',
    ),
    // Keyed by dog id so a household with two dogs cannot carry one dog's revealed contact over
    // to the other — changing the key remounts, which also aborts any request in flight.
    customRow(
      'emergency',
      'Emergency contact',
      'emergency contact backup next of kin',
      <EmergencyContactRow key={dog.dogId} dogId={dog.dogId} name={name} />,
    ),
  ]

  return {
    id: 'owner',
    title: 'Owner & contacts',
    ask: 'Tell us at your next visit and we’ll update your contact details.',
    groups: [
      group('owner', null, rows, {
        headline: 'We have no way to reach you',
        help: 'A mobile number and an email address are what we need to tell you anything about your dog’s day.',
      }),
    ],
  }
}

function documentsSection(dog: DogPayload, name: string): ProfileSection {
  const documentRows = dog.record.documents.map((doc) =>
    customRow(
      `doc-${doc.id}`,
      clean(doc.label) ?? DOC_KIND_LABEL[doc.kind],
      `${doc.kind} ${doc.verified ? 'verified' : ''}`,
      <DocumentValue doc={doc} />,
    ),
  )

  const record = dog.record
  const recordRows: ProfileRow[] = [
    row('lastReview', 'You last confirmed this', day(record.lastReviewAt)),
    row('nextReview', 'Next check due', day(record.nextReviewDue)),
    row('updated', 'Record last updated', day(record.updatedAt)),
  ]

  return {
    id: 'documents',
    title: 'Documents',
    ask: `Bring or email a document and we’ll add it to ${name}’s record.`,
    groups: [
      group('documents', 'On file', documentRows, {
        headline: 'No documents on file yet',
        help: 'The vaccination card is the one we need — bring it to your next visit and we’ll add it here.',
      }),
      group('record', 'This record', recordRows, {
        headline: 'This record hasn’t been checked yet',
        help: 'We’ll go through it with you at your next visit, and the date will show here.',
      }),
    ],
  }
}

/**
 * The whole profile.
 *
 * Built as a `Record<SectionId, …>` on purpose: every tab is guaranteed a section at compile time,
 * which is the failure the prototype could only find by eye. Deliberately NOT memoised — the
 * prototype recomputed all of this on every render and a memo with an incomplete dependency list is
 * the exact stale-derived-value bug this project has already shipped twice. Forty rows is nothing.
 */
function buildSections(
  dog: DogPayload,
  owner: OwnerDetails,
  name: string,
  today: Date,
): ProfileSection[] {
  const byId: Record<SectionId, ProfileSection> = {
    identity: identitySection(dog, name, today),
    medical: medicalSection(dog, name),
    care: careSection(dog, name),
    owner: ownerSection(dog, owner, name),
    documents: documentsSection(dog, name),
  }
  return SECTION_IDS.map((id) => byId[id])
}

// ── Search ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The searchable text of a row.
 *
 * An absent value is searched as "Not on file", which is deliberate: typing "not on file" is a fast
 * way to see every gap in the record, and searching "microchip" on a dog whose identity section is
 * completely blank still answers the question rather than returning nothing.
 */
function rowMatches(r: ProfileRow, term: string): boolean {
  return `${r.label} ${r.text ?? NOT_ON_FILE} ${r.keywords}`.toLowerCase().includes(term)
}

/**
 * Tab first, then free text. Searching narrows to the matching ROWS as well as the matching
 * sections — with roughly forty rows on "Everything", filtering whole cards only would still leave
 * the answer buried.
 *
 * A hit on the section's own title keeps all of its rows (the prototype's behaviour). A hit inside
 * a group drops that group's explanation copy, because boilerplate is not a search result.
 */
function filterSections(
  sections: readonly ProfileSection[],
  tab: TabId,
  term: string,
): ProfileSection[] {
  const tabbed = tab === 'all' ? [...sections] : sections.filter((section) => section.id === tab)
  if (term === '') return tabbed

  return tabbed.flatMap((section) => {
    if (section.title.toLowerCase().includes(term)) return [section]

    const groups = section.groups
      .map((g) => ({ ...g, rows: g.rows.filter((r) => rowMatches(r, term)) }))
      .filter((g) => g.rows.length > 0)

    return groups.length > 0 ? [{ ...section, groups }] : []
  })
}

function countRows(sections: readonly ProfileSection[]): number {
  return sections.reduce(
    (total, section) => total + section.groups.reduce((n, g) => n + g.rows.length, 0),
    0,
  )
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────────────────

const DOC_KIND_LABEL: Readonly<Record<RecordDocument['kind'], string>> = {
  'vaccination-card': 'Vaccination card',
  insurance: 'Insurance document',
  photo: 'Photo',
  signature: 'Signed agreement',
  other: 'Document',
}

/**
 * The magnifier (dc.html:180, 205).
 *
 * Inlined rather than taken from `UI_ICON_PATHS` because it mixes a `<circle>` with a `<path>` and
 * `<Icon>` only draws paths — an `<Icon>` fed this geometry would render an empty square.
 */
function SearchGlyph({ size }: { readonly size: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

/**
 * A care item's value: what the server derived, plus its urgency as a tag.
 *
 * Organic has no red/amber/green ramp, and inventing one would break the design system. Urgency is
 * `.tag-accent` (needs attention) against `.tag-accent-2` (fine) — the same two tags the reminders
 * and the front page use, so one item never reads as urgent on one screen and calm on another.
 */
function CareValue({ item }: { readonly item: CareItem }): ReactElement {
  const detail = item.detail.trim()
  return (
    <span style={VALUE_CLUSTER}>
      {detail.length > 0 ? <span>{detail}</span> : null}
      {item.hasStatus ? (
        <span className={item.urgency === 'attention' ? 'tag tag-accent' : 'tag tag-accent-2'}>
          {item.status}
        </span>
      ) : null}
    </span>
  )
}

function DocumentValue({ doc }: { readonly doc: RecordDocument }): ReactElement {
  const verifiedOn = day(doc.verifiedAt)
  const label = clean(doc.label) ?? DOC_KIND_LABEL[doc.kind]

  return (
    <span style={VALUE_CLUSTER}>
      {doc.verified ? (
        <span className="tag tag-accent-2" style={{ gap: '4px' }}>
          <Icon paths={UI_ICON_PATHS.check} size={12} />
          {verifiedOn === null ? 'Verified' : `Verified · ${verifiedOn}`}
        </span>
      ) : null}
      {isPortalPath(doc.url) ? (
        <a
          className="btn btn-ghost"
          style={GHOST_ACTION}
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          // Several rows say "Open"; without this a screen reader's link list is five identical items.
          aria-label={`Open ${label}`}
        >
          Open
        </a>
      ) : (
        // The contract says this cannot happen. If it ever does, the client sees a plain statement
        // and the URL stays off the page rather than becoming a public link to their documents.
        <span className="text-muted" style={ROW_VALUE_ABSENT}>
          Ask us for a copy
        </span>
      )}
    </span>
  )
}

type RevealState =
  | { readonly phase: 'hidden' }
  | { readonly phase: 'loading' }
  | { readonly phase: 'shown'; readonly contact: EmergencyContactPayload }
  | { readonly phase: 'failed'; readonly error: ApiFailure }

/**
 * The emergency contact, behind a deliberate act.
 *
 * This person never filled in a form and never saw a privacy notice. So: it is not in the payload,
 * it is fetched only when the client presses the button, every reveal is audited server-side, and
 * hiding it DISCARDS the data rather than parking it behind a boolean — a second look is a second
 * deliberate act, and the audit trail should say so.
 */
function EmergencyContactRow({
  dogId,
  name,
}: {
  readonly dogId: string
  readonly name: string
}): ReactElement {
  const [reveal, setReveal] = useState<RevealState>({ phase: 'hidden' })
  const inFlight = useRef<AbortController | null>(null)
  const panelId = `ft-emergency-${dogId}`

  useEffect(() => {
    return () => inFlight.current?.abort()
  }, [])

  const show = useCallback(() => {
    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller
    setReveal({ phase: 'loading' })

    void getEmergencyContact(dogId, { signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return
      if (result.ok) {
        setReveal({ phase: 'shown', contact: result.data })
        return
      }
      // The caller cancelled — an unmount or a dog switch, not a fault worth showing anyone.
      if (result.error.code === 'aborted') return
      setReveal({ phase: 'failed', error: result.error })
    })
  }, [dogId])

  const hide = useCallback(() => {
    inFlight.current?.abort()
    setReveal({ phase: 'hidden' })
  }, [])

  const shown = reveal.phase === 'shown'
  const loading = reveal.phase === 'loading'

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
      {/* Always present, so `aria-controls` always resolves and the reveal is announced in place. */}
      <span id={panelId} aria-live="polite" style={{ display: 'block' }}>
        {reveal.phase === 'hidden' ? (
          <span className="text-muted" style={ROW_VALUE_ABSENT}>
            Hidden until you ask
          </span>
        ) : null}
        {loading ? (
          <span className="text-muted" style={ROW_VALUE_ABSENT}>
            Fetching…
          </span>
        ) : null}
        {reveal.phase === 'failed' ? (
          <span className="text-muted" style={ROW_VALUE_ABSENT}>
            {reveal.error.message}
          </span>
        ) : null}
        {shown ? <ContactLines contact={reveal.contact} name={name} /> : null}
      </span>

      {/*
        Deliberately NOT `disabled` while the request is in flight. A disabled element cannot hold
        focus, so the browser would drop the keyboard user back to <body> mid-interaction — and the
        result they asked for then arrives somewhere they are no longer standing. `aria-busy` says
        the same thing without moving anyone, and the handler ignores a second press itself.
      */}
      <button
        type="button"
        className="btn btn-secondary"
        style={{ ...GHOST_ACTION, fontSize: '13px' }}
        onClick={() => {
          if (loading) return
          if (shown) hide()
          else show()
        }}
        aria-expanded={shown}
        aria-controls={panelId}
        aria-busy={loading}
      >
        {shown ? 'Hide' : 'Show'}
      </button>

      <span
        className="text-muted"
        style={{ fontSize: '12px', fontWeight: '400', maxWidth: '36ch', textAlign: 'right' }}
      >
        These are someone else’s details, so we keep them covered and note each time they’re shown.
      </span>
    </span>
  )
}

function ContactLines({
  contact,
  name,
}: {
  readonly contact: EmergencyContactPayload
  readonly name: string
}): ReactElement {
  const who = clean(contact.name)
  const phone = clean(contact.phone)
  const email = clean(contact.email)
  const where = joined([clean(contact.address), clean(contact.postcode)], ', ')

  if (who === null && phone === null && email === null && where === null) {
    return (
      <span className="text-muted" style={ROW_VALUE_ABSENT}>
        No emergency contact on file for {name}
      </span>
    )
  }

  return (
    <span style={{ display: 'block' }}>
      {who === null ? null : <span style={{ display: 'block' }}>{who}</span>}
      {phone === null ? null : <span style={{ display: 'block' }}>{phone}</span>}
      {email === null ? null : <span style={{ display: 'block' }}>{email}</span>}
      {where === null ? null : (
        <span className="text-muted" style={{ display: 'block', fontWeight: '400', fontSize: '13px' }}>
          {where}
        </span>
      )}
    </span>
  )
}

/**
 * The dog's own photograph, when one is on file.
 *
 * A PORTRAIT, NOT AN AVATAR. This was a 46px circle tucked against the right edge of the header —
 * the size a system uses for "which account am I signed in as", which is not the question this
 * page answers. It is the client's own dog, on the page about their own dog, and it is the only
 * warm thing on a screen that is otherwise a list of fields.
 *
 * SQUARE WITH A GENEROUS RADIUS rather than a circle. A circle throws away the corners of every
 * photograph, which at this size is most of the dog; the rounded square keeps them and matches the
 * radius the cards on this page already use. `object-fit: cover` with a 1:1 box means the image is
 * cropped but NEVER stretched, whatever aspect the client uploaded.
 *
 * `object-position` is CENTRED. It used to be `50% 62%`, copied from the prototype where it was
 * tuned to one specific photograph — HomeScreen's own comment records that this crop "cuts heads
 * off" on arbitrary uploads and was fixed there. It was never fixed here, and enlarging the image
 * would have made a head-cropping bug four times more visible.
 *
 * `alt=""` because it is decoration — the dog's name is already the page's heading, and "photo of
 * Milo" announced before it is noise. `.washed` is not optional: the design system requires every
 * content photograph to go through it. If the file 404s the element removes itself, because a
 * broken-image glyph in the header of a client's own record looks like a broken portal.
 */
function DogPhoto({ url }: { readonly url: string }): ReactElement | null {
  const [broken, setBroken] = useState(false)
  if (broken) return null
  return (
    <img
      className="washed"
      src={url}
      alt=""
      onError={() => setBroken(true)}
      style={{
        // Scales with the viewport between a phone and a desktop, so it is never a postage stamp
        // on a large screen nor half the page on a small one.
        width: 'clamp(112px, 21vw, 168px)',
        aspectRatio: '1',
        flex: 'none',
        borderRadius: 'calc(var(--radius-lg) * 1.15)',
        objectFit: 'cover',
        objectPosition: '50% 50%',
        // The ring reads as a mount rather than a border, which is what stops a photograph looking
        // pasted onto the page.
        border: '4px solid var(--color-bg)',
        boxShadow: 'var(--shadow-md)',
      }}
    />
  )
}

function GroupEmptyBlock({ copy }: { readonly copy: EmptyCopy }): ReactElement {
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '14px 0 6px' }}>
      <span
        aria-hidden="true"
        style={{
          width: '34px',
          height: '34px',
          flex: 'none',
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--color-neutral-200)',
          color: 'var(--color-neutral-700)',
        }}
      >
        <Icon paths={UI_ICON_PATHS.message} size={17} />
      </span>
      <span>
        <strong style={{ display: 'block', fontSize: '14.5px' }}>{copy.headline}</strong>
        <span className="text-muted" style={{ display: 'block', fontSize: '13px', marginTop: '2px' }}>
          {copy.help}
        </span>
      </span>
    </div>
  )
}

function RowLine({ r }: { readonly r: ProfileRow }): ReactElement {
  return (
    <div style={ROW}>
      <div className="text-muted" style={ROW_LABEL}>
        {r.label}
      </div>
      <div style={ROW_VALUE}>
        {r.node !== null ? (
          r.node
        ) : r.text === null ? (
          <span className="text-muted" style={ROW_VALUE_ABSENT}>
            {NOT_ON_FILE}
          </span>
        ) : (
          r.text
        )}
      </div>
    </div>
  )
}

function SectionCard({
  section,
  searching,
  onAsk,
}: {
  readonly section: ProfileSection
  /** True while a search term is narrowing the rows — see `showsEmptyCopy`. */
  readonly searching: boolean
  readonly onAsk: (section: ProfileSection) => void
}): ReactElement {
  return (
    <section className="card elev-sm" style={CARD}>
      <div style={CARD_HEAD}>
        <h4 style={{ margin: '0', fontSize: '19px' }}>{section.title}</h4>
        <button
          type="button"
          className="btn btn-ghost"
          style={GHOST_ACTION}
          onClick={() => onAsk(section)}
        >
          Something wrong?
        </button>
      </div>
      {section.groups.map((g) => (
        <div key={g.key}>
          {g.caption === null ? null : <div style={GROUP_CAPTION}>{g.caption}</div>}
          {showsEmptyCopy(g, searching) ? (
            <GroupEmptyBlock copy={g.empty} />
          ) : (
            g.rows.map((r) => <RowLine key={r.key} r={r} />)
          )}
        </div>
      ))}
    </section>
  )
}

/** Loading, failed and no-dog all use the same quiet card — none of them is a designed absence. */
function StateCard({
  headline,
  help,
  action,
}: {
  readonly headline: string
  readonly help: string
  readonly action: ReactNode
}): ReactElement {
  return (
    <section className="card elev-sm" style={{ alignItems: 'center', textAlign: 'center', gap: '0', padding: '36px 26px' }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '21px' }}>{headline}</h4>
      <p className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 18px', fontSize: '14px' }}>
        {help}
      </p>
      {action}
    </section>
  )
}

// ── The screen ──────────────────────────────────────────────────────────────────────────────────

export function ProfileScreen(): ReactElement {
  const { state, me, setTab, setSearch, clearSearch, showToast } = usePortal()
  const dog = useSelectedDog()
  const tabRefs = useRef(new Map<TabId, HTMLButtonElement | null>())

  /**
   * Arrow keys move between tabs, as the ARIA tabs pattern requires — the strip is one tab stop,
   * not six, so a keyboard user is not made to walk past every filter to reach the record.
   */
  const onTabKeys = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const here = TAB_IDS.indexOf(state.tab)
      let index = -1
      if (event.key === 'ArrowRight') index = (here + 1) % TAB_IDS.length
      else if (event.key === 'ArrowLeft') index = (here - 1 + TAB_IDS.length) % TAB_IDS.length
      else if (event.key === 'Home') index = 0
      else if (event.key === 'End') index = TAB_IDS.length - 1
      else return

      const next = TAB_IDS[index]
      if (next === undefined) return
      event.preventDefault()
      setTab(next)
      tabRefs.current.get(next)?.focus()
    },
    [state.tab, setTab],
  )

  if (me.status === 'loading') {
    return (
      <section style={{ animation: 'ft-rise .4s ease both' }}>
        <StateCard
          headline="Fetching your dog’s record"
          help="One moment."
          action={<span role="status" aria-live="polite" className="text-muted" style={{ fontSize: '13px' }}>Loading</span>}
        />
      </section>
    )
  }

  // A failed load is NOT an empty household. Saying "you have no dogs" to someone whose connection
  // dropped is the single worst thing this screen could do, so the error keeps its own shape.
  if (me.status === 'error' || me.data === null) {
    return (
      <section style={{ animation: 'ft-rise .4s ease both' }}>
        <StateCard
          headline="We couldn’t load the record"
          help={me.error?.message ?? 'Something went wrong at our end. Please try again in a moment.'}
          action={
            <button type="button" className="btn btn-secondary" style={{ minHeight: '44px' }} onClick={me.refresh}>
              Try again
            </button>
          }
        />
      </section>
    )
  }

  if (dog === null) {
    return (
      <section style={{ animation: 'ft-rise .4s ease both' }}>
        <StateCard
          headline="No dog on your account yet"
          help="If that doesn’t sound right, tell us and we’ll link your dog’s record to your account."
          action={null}
        />
      </section>
    )
  }

  const payload = me.data
  const name = clean(dog.name) ?? 'your dog'
  // The server's date, not the device's: a wrong clock must not age a dog or move a due date.
  const today = parseDay(payload.serverDate) ?? todayAtNoon()

  const sections = buildSections(dog, payload.owner, name, today)
  const typed = state.search.trim()
  const term = typed.toLowerCase()
  const visible = filterSections(sections, state.tab, term)
  const rowCount = countRows(visible)
  const noResults = visible.length === 0

  const searchCount =
    typed === ''
      ? ''
      : rowCount > 0
        ? `${rowCount} ${rowCount === 1 ? 'detail matches' : 'details match'} “${typed}”`
        : `No details match “${typed}”`

  const photo = dog.record.documents.find((doc) => doc.kind === 'photo' && isPortalPath(doc.url))

  return (
    <section style={{ animation: 'ft-rise .4s ease both' }}>
      <header
        style={{
          display: 'flex',
          // Centred against a portrait rather than top-aligned against an avatar: at this size a
          // flush top edge leaves the photograph hanging below the text with nothing under it.
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          // Wraps rather than crushing the heading on a narrow phone. `flex: 1 1 240px` on the
          // text block is what decides when: below roughly 240px of room it takes its own line.
          flexWrap: 'wrap',
          marginBottom: '20px',
        }}
      >
        <div style={{ flex: '1 1 240px' }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: '700',
              letterSpacing: '.09em',
              textTransform: 'uppercase',
              color: 'var(--color-accent-700)',
              marginBottom: '7px',
            }}
          >
            {name}’s record
          </div>
          <h1 style={{ margin: '0 0 7px', fontSize: 'clamp(30px, 5.5vw, 42px)' }}>Dog profile</h1>
          <p className="text-muted" style={{ margin: '0', fontSize: '15px' }}>
            A living record — everything stays saved between visits. Spot something wrong? Tell us at
            your next visit and we’ll put it right.
          </p>
        </div>
        {photo === undefined ? null : <DogPhoto key={photo.url} url={photo.url} />}
      </header>

      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '16px',
            top: '0',
            bottom: '0',
            display: 'grid',
            placeItems: 'center',
            color: 'var(--color-neutral-600)',
          }}
        >
          <SearchGlyph size={18} />
        </span>
        <input
          className="input"
          id="ft-search"
          type="search"
          value={state.search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={`Search ${name}’s profile`}
          placeholder="Search — try “vet”, “food” or “microchip”"
          style={{ width: '100%', paddingLeft: '46px' }}
        />
      </div>

      {/* min-height reserves the line so the list below does not jump on every keystroke. */}
      <p
        className="text-muted"
        role="status"
        aria-live="polite"
        style={{ margin: '0 0 12px 4px', fontSize: '12.5px', minHeight: '18px' }}
      >
        {searchCount}
      </p>

      <div
        className="ft-scroll"
        role="tablist"
        aria-label="Profile sections"
        style={{ display: 'flex', gap: '8px', overflowX: 'auto', padding: '4px 2px', marginBottom: '16px' }}
      >
        {TAB_IDS.map((id) => {
          const selected = state.tab === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              id={`ft-tab-${id}`}
              ref={(element) => {
                tabRefs.current.set(id, element)
              }}
              aria-selected={selected}
              aria-controls="ft-profile-sections"
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(id)}
              onKeyDown={onTabKeys}
              style={selected ? TAB_SELECTED : TAB_BASE}
            >
              {TAB_LABEL[id]}
            </button>
          )
        })}
      </div>

      <div id="ft-profile-sections" role="tabpanel" aria-labelledby={`ft-tab-${state.tab}`}>
        {visible.map((section) => (
          <SectionCard
            key={section.id}
            section={section}
            searching={term !== ''}
            onAsk={(chosen) => showToast(chosen.ask)}
          />
        ))}

        {noResults ? (
          <section className="card elev-sm" style={{ alignItems: 'center', textAlign: 'center', gap: '0', padding: '36px 26px' }}>
            <span
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'var(--color-neutral-200)',
                color: 'var(--color-neutral-700)',
                marginBottom: '14px',
              }}
            >
              <SearchGlyph size={24} />
            </span>
            <h4 style={{ margin: '0 0 6px', fontSize: '21px' }}>Nothing found</h4>
            <p className="text-muted" style={{ maxWidth: '40ch', margin: '0 0 18px', fontSize: '14px' }}>
              {typed === ''
                ? 'This section is empty for now.'
                : `Nothing in ${name}’s record matches “${typed}”.`}
            </p>
            <button type="button" className="btn btn-secondary" style={{ minHeight: '44px' }} onClick={clearSearch}>
              Clear search and filters
            </button>
          </section>
        ) : null}
      </div>
    </section>
  )
}
